import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getRelatedProductsCollectionV2 } from "@insite/client-framework/Services/ProductServiceV2";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Button from "@insite/mobius/Button";
import Icon from "@insite/mobius/Icon";
import Trash from "@insite/mobius/Icons/Trash";
import Select from "@insite/mobius/Select";
import Typography from "@insite/mobius/Typography";
import CustomRailDialog, { CustomIncludeModalConfig, CustomIncludeValues, PreconfiguredValues, RailValidationConfig } from "@thkStore/Components/CustomProducts/CustomRailDialog";
import { IS_CUSTOM_PRODUCT } from "@thkStore/Constants/ProductPropertiesKeys";
import { ClipboardCustomProductState, ClipboardProductState } from "@thkStore/Store/Widgets/Clipboard/ClipboardState";
import IncludeCustomProductCard from "@thkStore/Widgets/ClipboardSummary/IncludeCustomProductCard";
import IncludeProductCard from "@thkStore/Widgets/ClipboardSummary/IncludeProductCard";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";

const SelectorWrapper = styled.div`
    padding: 8px 0;
`;

const SelectedList = styled.div`
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const HeaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    span {

        &[class^="TypographyStyle-sc-"] {
            font-size: 14px;
            text-transform: capitalize;
        }
    
        &[class^="IconWrapper-sc-"] {
            cursor: pointer;
        }
    }
`;

interface Props {
    parentProductId: string;
    includesText: string | undefined;
    selectedIncludes: { [includeProductId: string]: ClipboardProductState };
    selectedCustomIncludes: { [includeProductId: string]: ClipboardCustomProductState };
    customIncludeModalConfig: CustomIncludeModalConfig;
    onAddInclude: (includeProductId: string, productName: string) => void;
    onChangeIncludeQty: (includeProductId: string, productName: string, qty: number) => void;
    onRemoveInclude: (includeProductId: string) => void;
    onAddCustomInclude: (localId: string, includeProductId: string, productName: string, unitPrice: number, weight: number, length: number, g1: number, g2: number, daysToShip: string) => void;
    onChangeCustomInclude: (parentProductId: string, localId: string, productName: string, qty: number, unitPrice: number, weight: number, length: number, g1: number, g2: number, daysToShip: string) => void;
    onRemoveCustomInclude: (localId: string) => void;
}

const IncludesSelector: React.FC<Props> = ({
    parentProductId,
    includesText,
    selectedIncludes,
    selectedCustomIncludes,
    customIncludeModalConfig,
    onAddInclude,
    onChangeIncludeQty,
    onRemoveInclude,
    onAddCustomInclude,
    onChangeCustomInclude,
    onRemoveCustomInclude,
}) => {
    const [availableProducts, setAvailableProducts] = useState<ProductModel[]>([]);
    const [customProductTemplate, setCustomProductTemplate] = useState<ProductModel | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
    const [customProductUnitOfMeasureDisplay, setCustomProductUnitOfMeasureDisplay] = useState("");
    const [customIncludeToEdit, setCustomIncludeToEdit] = useState<PreconfiguredValues | null>(null);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);

        const fetchAllIncludes = async () => {
            const allProducts: ProductModel[] = [];
            let page = 1;
            let numberOfPages = 1;

            do {
                const result = await getRelatedProductsCollectionV2({
                    productId: parentProductId,
                    relationship: "Includes",
                    expand: ["attributes", "properties"],
                    includeAttributes: ["includeOnProduct"],
                    pageSize: 36,
                    page,
                });

                if (cancelled) {
                    return;
                }

                if (result.products) {
                    allProducts.push(...result.products);
                }

                numberOfPages = result.pagination?.numberOfPages ?? 1;
                page++;
            } while (page <= numberOfPages);

            if (!cancelled) {
                const customProduct = allProducts.find(
                    p => p.properties[IS_CUSTOM_PRODUCT] === "true",
                );

                if (customProduct) {
                    const idx = allProducts.indexOf(customProduct);
                    allProducts.splice(idx, 1);
                }

                setCustomProductTemplate(customProduct || null);                
                setAvailableProducts(allProducts);
                setCustomProductUnitOfMeasureDisplay(customProduct?.unitOfMeasures ? customProduct.unitOfMeasures[0].unitOfMeasureDisplay : ""); // Assuming all products have the same unit of measure, otherwise this needs to be handled differently
            }
        };

        fetchAllIncludes().finally(() => {
            if (!cancelled) {
                setIsLoading(false);
            }
        });

        return () => { cancelled = true; };
    }, [parentProductId]);

    const selectedIds = new Set(Object.keys(selectedIncludes));
    const filteredOptions = availableProducts.filter(p => !selectedIds.has(p.id));
    const showSelect = isLoading || filteredOptions.length > 0;

    const handleSelection = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const productId = event.target.value;
        if (!productId) {
            return;
        }
        const product = availableProducts.find(p => p.id === productId);
        if (product) {
            onAddInclude(product.id, product.productTitle);
        }
    };

    const handleCustomIncludeConfirm = useCallback((values: CustomIncludeValues) => {
        if (customProductTemplate) {
            onAddCustomInclude(
                values.localId,
                customProductTemplate.id,
                values.partNo,
                values.unitPrice,
                values.weight,
                values.length,
                values.g1,
                values.g2,
                values.daysToShip,
            );
        }
        setCustomIncludeToEdit(null);
        setIsCustomModalOpen(false);
    }, [customProductTemplate, onAddCustomInclude]);

    const handleCustomIncludeCancel = useCallback(() => {
        setCustomIncludeToEdit(null);
        setIsCustomModalOpen(false);
    }, []);

    const railValidationConfig: RailValidationConfig | undefined = useMemo(() => {
        if (!customProductTemplate) {
            return undefined;
        }
        const props = customProductTemplate.properties;
        return {
            minimumLength: parseFloat(props["minimumLength"]) || 0,
            maximumLength: parseFloat(props["maximumLength"]) || 0,
            lengthIncrement: parseFloat(props["lengthIncrement"]) || 0,
            g1Minimum: parseFloat(props["g1Minimum"]) || 0,
            g1Maximum: parseFloat(props["g1Maximum"]) || 0,
            g1Increment: parseFloat(props["g1Increment"]) || 0,
            g2Minimum: parseFloat(props["g2Minimum"]) || 0,
            g2Maximum: parseFloat(props["g2Maximum"]) || 0,
            g2Increment: parseFloat(props["g2Increment"]) || 0,
            holePitchF: parseFloat(props["holePitchF"]) || 0,
            holeDiameter: parseFloat(props["holeDiameterD2"]) || 0,
        };
    }, [customProductTemplate]);

    const selectedProductsMap = new Map(availableProducts.map(p => [p.id, p]));

    const titleContent = (
        <HeaderWrapper>
            <Typography>{translate(`${includesText}`)} ({Object.keys(selectedIncludes).length + Object.keys(selectedCustomIncludes).length})</Typography>
            <Icon src={Trash} size={16}/>
        </HeaderWrapper>
    );

    const headerProps = {
        css: css`
            margin-bottom: 8px;

            button {
                color: ${({ theme }) => theme.colors.text.main};
                background-color: #EBEBEB;
                flex-direction: row-reverse;
                justify-content: flex-end;
                gap: 5px;
                padding: 8px;
                border: none !important;

                &:focus {
                    padding: 8px;
                    border: none !important;
                }

                span {
                    font-weight: normal;
                }
            }
        `,
    };

    const panelProps = {
        css: css`
            background-color: ${({ theme }) => theme.colors.primary.contrast};
            padding: 8px;
            margin-top: -8px;
            border: none;

            .select-rail {
                display: flex;
                gap: 8px;

                button {
                    border-radius: 7px;
                    width: 85%;
                }
            }
        `,
    };

    return (
        <ManagedAccordionSection title={titleContent} initialExpanded={true} headerProps={headerProps} panelProps={panelProps}>
            <SelectorWrapper>
                <StyledWrapper className="select-rail">
                    {showSelect && (
                        <Select
                            value=""
                            onChange={handleSelection}
                            disabled={isLoading}
                            data-test-selector={`includesSelect_${parentProductId}`}
                        >
                            <option value="">
                                {isLoading ? translate("Loading...") : translate("Select")}
                            </option>
                            {filteredOptions.map(product => (
                                <option key={product.id} value={product.id}>
                                    {product.productTitle}
                                </option>
                            ))}
                        </Select>
                    )}
                    {customProductTemplate && (
                        <>
                            <Button variant="primary" onClick={() => setIsCustomModalOpen(true)}>
                                {translate("Custom Include")}
                            </Button>
                            <CustomRailDialog
                                isOpen={isCustomModalOpen}
                                config={customIncludeModalConfig}
                                preconfiguredValues={customIncludeToEdit}
                                validationConfig={railValidationConfig!}
                                customProductTemplateId={customProductTemplate.id}
                                parentProductId={parentProductId}
                                source="Include"
                                onConfirm={handleCustomIncludeConfirm}
                                onCancel={handleCustomIncludeCancel}
                            />
                        </>
                    )}                    
                </StyledWrapper>
                {Object.keys(selectedIncludes).length > 0 && (
                    <SelectedList>
                        {Object.entries(selectedIncludes).map(([includeId, include]) => {
                            const product = selectedProductsMap.get(includeId);
                            if (!product) {
                                return (
                                    <Typography key={includeId} size={13}>
                                        {include.productName} ({translate("qty")}: {include.qtyOrdered})
                                    </Typography>
                                );
                            }
                            return (
                                <IncludeProductCard
                                    key={includeId}
                                    product={product}
                                    qtyOrdered={include.qtyOrdered}
                                    onQuantityChange={(qty) => onChangeIncludeQty(includeId, include.productName, qty)}
                                    onRemove={() => onRemoveInclude(includeId)}
                                />
                            );
                        })}
                    </SelectedList>
                )}
                {Object.keys(selectedCustomIncludes).length > 0 && (
                    <SelectedList>
                        {Object.entries(selectedCustomIncludes).map(([localId, include]) => {                            
                            return (
                                <IncludeCustomProductCard
                                    key={localId}
                                    customProductId={include.productId}
                                    productName={include.productName}
                                    daysToShip={include.daysToShip}                                    
                                    unitPrice={include.unitPrice}
                                    unitOfMeasureDisplay={customProductUnitOfMeasureDisplay}
                                    qtyOrdered={include.qtyOrdered}
                                    onQuantityChange={(qty) => onChangeCustomInclude(localId, include.productId, include.productName, qty, include.unitPrice, include.weight, include.length, include.g1, include.g2, include.daysToShip)}
                                    onRemove={() => onRemoveCustomInclude(localId)}
                                    editCustomInclude={() => {                                        
                                        setCustomIncludeToEdit({
                                            localId,
                                            length: include.length,
                                            g1: include.g1,
                                            g2: include.g2,
                                        });
                                        setIsCustomModalOpen(true);
                                    }}
                                />
                            );
                        })}
                    </SelectedList>
                )}
            </SelectorWrapper>
        </ManagedAccordionSection>
    );
};

export default IncludesSelector;
